import React from 'react';
import { ShieldCheck, AlertTriangle, XCircle, Info } from 'lucide-react';

const rupees = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (d) => {
  if (!d) return '-';
  const dt = new Date(d);
  return `${String(dt.getDate()).padStart(2, '0')}-${String(dt.getMonth() + 1).padStart(2, '0')}-${dt.getFullYear()}`;
};

// Result of the import check (see utils/entryFlags.js): what is wrong, what the importer
// guessed, what it left out, and whether the invoice total still matches Tally's.
//   errors / warnings - from flagEntries (+ invoice-level items)
//   acknowledged      - the "I have checked" box is ticked
//   confirmed         - the warnings are settled (ticked, or a reopened invoice that was checked
//                       when saved) and no real problem is left: they are shown as done, collapsed,
//                       instead of as something still wrong
const ImportCheckPanel = ({ errors, warnings, skipped = [], importTotal, invoiceTotal, acknowledged, confirmed = false, onAcknowledge, trusted }) => {
  const skippedWarn = skipped.filter(s => s.level === 'warn');
  const skippedInfo = skipped.filter(s => s.level !== 'warn');
  const reconcilable = importTotal !== null && importTotal !== undefined;
  const diff = reconcilable ? Math.round((invoiceTotal - importTotal) * 100) / 100 : 0;
  const itemCount = warnings.length + skippedWarn.length;
  const hasItems = itemCount > 0;
  const settled = confirmed && hasItems;
  const good = !errors.length && (!hasItems || settled);

  // A line-level item names its voucher; an invoice-level one (discount, totals) is just a message
  const Row = ({ item }) => (
    item.scope === 'invoice' ? (
      <li className="text-xs sm:text-sm text-slate-700">{item.message}</li>
    ) : (
      <li className="font-mono text-xs sm:text-sm">
        {fmtDate(item.date)} &nbsp;{item.voucher_no} &nbsp;veh {item.vehicle_no} &nbsp;{rupees(item.amount)}
        <span className="font-sans text-slate-600"> - {item.message}</span>
      </li>
    )
  );

  const itemLists = (
    <>
      {skippedWarn.length > 0 && (
        <div>
          <p className="font-semibold text-amber-700 mb-1 flex items-center gap-1"><AlertTriangle className="w-4 h-4" /> Vouchers in the file that were NOT imported and may change what is owed:</p>
          <ul className="space-y-1">
            {skippedWarn.map((s, i) => (
              <li key={`s${i}`} className="font-mono text-xs sm:text-sm">{fmtDate(s.business_date)} &nbsp;{s.type} &nbsp;{s.voucher_no || '-'} &nbsp;{s.amount !== null ? rupees(s.amount) : ''} <span className="font-sans text-slate-600">{s.ledger}</span></li>
            ))}
          </ul>
        </div>
      )}

      {warnings.length > 0 && (
        <div>
          <p className="font-semibold text-amber-700 mb-1">The importer was not sure about these:</p>
          <ul className="space-y-1">{warnings.map((x, i) => <Row key={`w${i}`} item={x} />)}</ul>
        </div>
      )}
    </>
  );

  return (
    <div className="border-t border-slate-200">
      <div className={`px-5 py-3 flex items-center gap-2 ${errors.length ? 'bg-red-50 text-red-800' : good ? 'bg-emerald-50 text-emerald-800' : 'bg-amber-50 text-amber-800'}`}>
        {errors.length ? <XCircle className="w-5 h-5 shrink-0" /> : good ? <ShieldCheck className="w-5 h-5 shrink-0" /> : <AlertTriangle className="w-5 h-5 shrink-0" />}
        <h3 className="font-bold">
          Import check: {errors.length ? `${errors.length} problem(s) must be fixed before printing`
            : !hasItems ? 'all lines understood'
              : settled ? `${itemCount} item(s) confirmed${trusted ? ' (checked when this invoice was saved)' : ''}`
                : `${itemCount} item(s) to confirm`}
        </h3>
      </div>

      <div className="px-5 py-3 space-y-3 text-sm">
        {reconcilable && (
          <div className={`flex flex-wrap gap-x-6 gap-y-1 ${diff === 0 ? 'text-emerald-700' : 'text-amber-700'}`}>
            <span>Tally sales imported: <b>{rupees(importTotal)}</b></span>
            <span>On this invoice: <b>{rupees(invoiceTotal)}</b></span>
            <span>{diff === 0 ? 'Matches' : `Difference ${rupees(diff)} - lines were edited or deleted after import`}</span>
          </div>
        )}

        {errors.length > 0 && (
          <div>
            <p className="font-semibold text-red-700 mb-1">Fix these (printing and saving are blocked):</p>
            <ul className="space-y-1">{errors.map((x, i) => <Row key={`e${i}`} item={x} />)}</ul>
          </div>
        )}

        {settled ? (
          <details className="text-xs text-slate-500">
            <summary className="cursor-pointer select-none">Show the {itemCount} item(s) that were confirmed</summary>
            <div className="mt-2 space-y-3 text-sm">{itemLists}</div>
          </details>
        ) : itemLists}

        {skippedInfo.length > 0 && (
          <p className="text-slate-500 flex items-start gap-1 text-xs">
            <Info className="w-4 h-4 shrink-0" />
            Not imported (not sales): {skippedInfo.map(s => `${s.type} ${s.voucher_no || ''} ${s.amount !== null ? rupees(s.amount) : ''} ${s.ledger}`.replace(/\s+/g, ' ').trim()).join('; ')}
          </p>
        )}

        {errors.length === 0 && hasItems && !trusted && (
          <label className="flex items-center gap-2 font-medium text-slate-800 cursor-pointer">
            <input type="checkbox" checked={acknowledged} onChange={e => onAcknowledge(e.target.checked)} />
            I have checked the items above
          </label>
        )}
      </div>
    </div>
  );
};

export default ImportCheckPanel;
